import React, { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Label, FormFeedback, Input, Row, Col } from 'reactstrap'
import Modals from './Modals'
import server from '../../server'

export default function PaymentModal({
  visible,
  setVisible,
  onSave,
  apiLoginID,
  clientKey,
  pharmacyId,
  userId,
}) {
  const [httpsError, setHttpsError] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [paymentProfile, setPaymentProfile] = useState(null)
  const [paymentProfileLoading, setPaymentProfileLoading] = useState(false)
  const [pharmacyIdData, setPharmacyIdData] = useState(null)
  console.log(pharmacyId)
  useEffect(() => {
    if (pharmacyId) {
      fetchPaymentProfile(pharmacyId)
    }
  }, [pharmacyId])

  const fetchPaymentProfile = async (pharmacyId) => {
    setPaymentProfileLoading(true)
    try {
      const response = await server.get(
        `/payments/pharmacy-payment-profile/${pharmacyId}`)
      console.log(response?.data)
      setPaymentProfile(response?.data)
      setPaymentProfileLoading(false)
    } catch (error) {
      // 404 is expected when pharmacy has no payment profile yet
      if (error.response?.status !== 404) {
        console.error('Error fetching payment profile:', error)
      }
      setPaymentProfile(null)
      setPaymentProfileLoading(false)
    }
  }

  useEffect(() => {
    if (
      window.location.protocol !== 'https:' &&
      window.location.hostname !== 'localhost'
    ) {
      setHttpsError(true)
    }

    // const acceptJsUrl ='https://jstest.authorize.net/v1/Accept.js'
    const acceptJsUrl="https://js.authorize.net/v1/Accept.js" //!Production
      

    const script = document.createElement('script')
    script.src = acceptJsUrl
    script.async = true
    document.body.appendChild(script)

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script)
      }
    }
  }, [])

  const validationSchema = Yup.object({
    cardholderName: Yup.string().required('Cardholder name is required'),
    cardNumber: Yup.string()
      .required('Card number is required')
      .matches(/^[0-9]{16}$/, 'Card number must be 16 digits'),
    cvv: Yup.string()
      .required('CVV is required')
      .matches(/^[0-9]{3,4}$/, 'CVV must be 3 or 4 digits'),
    expiryMonth: Yup.string()
      .required('Expiry month is required')
      .matches(/^(0[1-9]|1[0-2])$/, 'Must be a valid month (01-12)'),
    expiryYear: Yup.string()
      .required('Expiry year is required')
      .matches(/^20[2-9][0-9]$/, 'Must be a valid 4-digit year'),
  })

  const formik = useFormik({
    initialValues: {
      cardholderName: '',
      cardNumber: '',
      cvv: '',
      expiryMonth: '',
      expiryYear: '',
    },
    validationSchema,
    onSubmit: (values) => {
      setIsProcessing(true)
      setPaymentError('')

      if (window.Accept) {
        const secureData = {
          authData: {
            clientKey,
            apiLoginID,
          },
          cardData: {
            cardNumber: values.cardNumber,
            month: values.expiryMonth,
            year: values.expiryYear,
            cardCode: values.cvv,
            fullName: values.cardholderName,
          },
        }

        window.Accept.dispatchData(secureData, responseHandler)
      } else {
        setPaymentError('Payment service is unavailable. Accept.js not loaded.')
        setIsProcessing(false)
      }
    },
  })

  const responseHandler = async (response) => {
    setIsProcessing(false)
    console.log('Authorize.Net Accept.js response:', response)

    // First check if there was an error from Authorize.Net
    if (response.messages.resultCode === 'Error') {
      let errorMessage = 'Unknown error occurred'
      if (response.messages.message?.length > 0) {
        errorMessage = response.messages.message[0].text
      }
      setPaymentError(errorMessage)
      return
    }

    try {
      const data = {
        ...response.opaqueData,
        userId: userId,
        cardholderName: formik.values.cardholderName,
        pharmacyId: pharmacyId, // Include pharmacyId in the request
      }
      console.log('Sending payment data:', data)

      // Add authorization header to all API requests
      const responseToken = await server.post(
        '/payments/tokenized-payment-profile',
        data,
        {
        }
      )

      if (!responseToken.data || !responseToken.data.data) {
        throw new Error('Invalid response from server')
      }

      console.log('Payment token response:', responseToken.data)
      const token = responseToken.data.data

      const sentData = await server.post(
        '/payments/pharmacy-payment-profile-ids',
        token,
        {
        }
      )

      console.log('Profile response:', sentData)
      setPharmacyIdData(sentData?.pharmacyId)

      // Process successful payment
      const maskedCardNumber = `****${formik.values.cardNumber.slice(-4)}`
      const formattedExpiry = `${
        formik.values.expiryMonth
      }/${formik.values.expiryYear.slice(-2)}`

      onSave({
        cardholderName: formik.values.cardholderName,
        cardNumber: maskedCardNumber,
        paymentNonce: response?.opaqueData?.dataValue,
        paymentDescriptor: response?.opaqueData?.dataDescriptor,
        expiryDate: formattedExpiry,
        expiryMonth: formik.values.expiryMonth,
        expiryYear: formik.values.expiryYear,
        pharmacyId,
      })

      setVisible(false)
      formik.resetForm()
    } catch (error) {
      console.error('Payment processing error:', error)
      const errorMessage =
        error.response?.data?.message ||
        'Failed to process payment. Please try again.'
      setPaymentError(errorMessage)
    }
  }

  const formatCardNumber = (e) => {
    let value = e.target.value.replace(/\s/g, '')
    if (value.length > 16) value = value.slice(0, 16)
    formik.setFieldValue('cardNumber', value)
  }

  return (
    <Modals
      ModalHeader='Add Card'
      btnName='Save Card'
      visible={visible}
      setVisible={setVisible}
      onButtonClick={() => formik.handleSubmit()}
      disabled={isProcessing}
      loading={isProcessing}
    >
      <div className='p-2'>
        {httpsError && (
          <div className='alert alert-warning mb-3'>
            <strong>Warning:</strong> HTTPS connection is required for payment
            processing. Your current connection is not secure. Testing is still
            available on localhost.
          </div>
        )}

        {paymentProfileLoading && (
          <div className='text-center my-3'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
            <p className='mt-2'>Loading payment profile...</p>
          </div>
        )}

        {!paymentProfileLoading && paymentProfile?.cardNumber && (
          <div className='alert alert-info mb-3'>
            <strong>Existing Card:</strong> {paymentProfile.cardNumber}
            <br />
            <strong>Expires:</strong> {paymentProfile.expiryDate}
          </div>
        )}

        <form onSubmit={formik.handleSubmit}>
          {paymentError && (
            <div className='alert alert-danger mb-3'>{paymentError}</div>
          )}

          {/* Cardholder Name */}
          <div className='mb-3'>
            <Label for='cardholderName'>Cardholder Name</Label>
            <Input
              id='cardholderName'
              name='cardholderName'
              placeholder='Name on card'
              value={formik.values.cardholderName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={
                !!(
                  formik.touched.cardholderName && formik.errors.cardholderName
                )
              }
              disabled={isProcessing}
            />
            <FormFeedback>{formik.errors.cardholderName}</FormFeedback>
          </div>

          {/* Card Number */}
          <div className='mb-3'>
            <Label for='cardNumber'>Card Number</Label>
            <Input
              id='cardNumber'
              name='cardNumber'
              placeholder='1234 5678 9012 3456'
              value={formik.values.cardNumber}
              onChange={formatCardNumber}
              onBlur={formik.handleBlur}
              invalid={
                !!(formik.touched.cardNumber && formik.errors.cardNumber)
              }
              disabled={isProcessing}
            />
            <FormFeedback>{formik.errors.cardNumber}</FormFeedback>
          </div>

          {/* Expiration Date & CVV */}
          <Row>
            <Col md={6}>
              <div className='mb-3'>
                <Label>Expiration Date</Label>
                <Row>
                  <Col xs={6}>
                    <Input
                      id='expiryMonth'
                      name='expiryMonth'
                      placeholder='MM'
                      value={formik.values.expiryMonth}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        !!(
                          formik.touched.expiryMonth &&
                          formik.errors.expiryMonth
                        )
                      }
                      disabled={isProcessing}
                    />
                    <FormFeedback>{formik.errors.expiryMonth}</FormFeedback>
                  </Col>
                  <Col xs={6}>
                    <Input
                      id='expiryYear'
                      name='expiryYear'
                      placeholder='YYYY'
                      value={formik.values.expiryYear}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        !!(
                          formik.touched.expiryYear && formik.errors.expiryYear
                        )
                      }
                      disabled={isProcessing}
                    />
                    <FormFeedback>{formik.errors.expiryYear}</FormFeedback>
                  </Col>
                </Row>
              </div>
            </Col>
            <Col md={6}>
              <div className='mb-3'>
                <Label for='cvv'>CVV</Label>
                <Input
                  id='cvv'
                  name='cvv'
                  placeholder='123'
                  value={formik.values.cvv}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.cvv && formik.errors.cvv)}
                  disabled={isProcessing}
                />
                <FormFeedback>{formik.errors.cvv}</FormFeedback>
              </div>
            </Col>
          </Row>
        </form>
      </div>
    </Modals>
  )
}
